import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useOutletContext, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/solid';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { format } from 'date-fns';
import type { Timestamp } from 'firebase/firestore';
import { Collaborator, CollaboratorAllocation } from '@/models';
import { ClientLogo } from '@/components/ClientLogo';
import { TanStackTableView } from '@/components/shared';
import {
  useCollaboratorAllocationsQuery,
  useDeleteCollaboratorAllocationMutation,
} from '@/features/Collaborators/Allocations/data';
import {
  AllocationBucketType,
  AllocationBucketTypes,
  AllocationType,
  AllocationTypes,
  AllocationStatuses,
  ALLOCATION_TYPE_METADATA,
} from '@/features/Collaborators/Models';
import { CollaboratorAvatar } from '@/features/Collaborators/Shared';
import { CollaboratorInfoHeader } from '@/features/Collaborators/Shared/CollaboratorInfoHeader';
import { ALLOCATION_TYPE_ICON_MAP } from '@/features/Collaborators/Allocations/constants/allocationTypeIcons';
import { useCollaboratorAllocationPreviewStatus } from '@/features/Collaborators/Allocations/hooks/useCollaboratorAllocationPreviewStatus';
import { formatCapacityLabel } from '@/features/Collaborators/Allocations/utils/formatCapacityLabel';
import { PAGE_TITLE } from './collaboratorAllocationsPage.definitions';
import { useToast } from '@/hooks/useToast';

type CollaboratorContext = {
  collaborators: Collaborator[];
  isLoading: boolean;
  error: Error | null;
};

const CAPACITY_SUFFIX_MAP: Record<AllocationBucketType, string> = {
  [AllocationBucketTypes.Defined]: 'horas totais',
  [AllocationBucketTypes.Daily]: 'horas/dia',
  [AllocationBucketTypes.Weekly]: 'horas/semana',
  [AllocationBucketTypes.Monthly]: 'horas/mês',
};

type AllocationDisplayStatus = 'Cancelled' | 'Finished' | 'Current' | 'Scheduled';

const ALLOCATION_STATUS_METADATA: Record<
  AllocationDisplayStatus,
  { label: string; badgeClass: string }
> = {
  Cancelled: {
    label: 'Cancelada',
    badgeClass: 'bg-red-100 text-red-700 border border-red-200',
  },
  Finished: {
    label: 'Concluída',
    badgeClass: 'bg-emerald-100 text-emerald-700 border border-emerald-200',
  },
  Current: {
    label: 'Vigente',
    badgeClass: 'bg-sky-100 text-sky-700 border border-sky-200',
  },
  Scheduled: {
    label: 'Agendada',
    badgeClass: 'bg-amber-100 text-amber-700 border border-amber-200',
  },
};

function formatTimestampValue(timestamp?: Timestamp | null): string {
  if (!timestamp || typeof timestamp.toDate !== 'function') {
    return 'Sem data';
  }

  try {
    return format(timestamp.toDate(), 'dd/MM/yyyy');
  } catch {
    return 'Sem data';
  }
}

function formatCapacity(bucket: number, bucketType: AllocationBucketType): string {
  if (!Number.isFinite(bucket)) {
    return 'Sem capacidade';
  }

  const suffix = CAPACITY_SUFFIX_MAP[bucketType] ?? 'horas';
  const formattedBucket = Number.isInteger(bucket)
    ? bucket.toString()
    : bucket.toLocaleString('pt-BR', {
      minimumFractionDigits: 1,
      maximumFractionDigits: 2,
    });

  return `${formattedBucket} ${suffix}`;
}

function resolveAllocationStatus(
  allocation: CollaboratorAllocation
): { status: AllocationDisplayStatus; label: string; className: string } | null {
  if (allocation.status === AllocationStatuses.Cancelled) {
    const meta = ALLOCATION_STATUS_METADATA.Cancelled;
    return { status: 'Cancelled', label: meta.label, className: meta.badgeClass };
  }

  const startDate = allocation.startDate?.toDate?.() ?? null;
  const endDate = allocation.endDate?.toDate?.() ?? null;
  if (!startDate) {
    return null;
  }

  const now = new Date();

  if (endDate && endDate < now) {
    const meta = ALLOCATION_STATUS_METADATA.Finished;
    return { status: 'Finished', label: meta.label, className: meta.badgeClass };
  }

  if (startDate <= now && (!endDate || endDate >= now)) {
    const meta = ALLOCATION_STATUS_METADATA.Current;
    return { status: 'Current', label: meta.label, className: meta.badgeClass };
  }

  if (startDate > now) {
    const meta = ALLOCATION_STATUS_METADATA.Scheduled;
    return { status: 'Scheduled', label: meta.label, className: meta.badgeClass };
  }

  return null;
}

export default function CollaboratorAllocationsPage() {
  const { id: collaboratorId } = useParams<{ id?: string }>();
  const {
    collaborators,
    isLoading: collaboratorsLoading,
    error: collaboratorsError,
  } = useOutletContext<CollaboratorContext>();
  const navigate = useNavigate();
  const {
    data: allocations,
    isLoading: loadingAllocations,
    isError: allocationsError,
    error: allocationsErrorDetail,
  } = useCollaboratorAllocationsQuery(collaboratorId);
  const deleteAllocationMutation = useDeleteCollaboratorAllocationMutation();
  const { success: toastSuccess, error: toastError } = useToast();
  const [deletingAllocationId, setDeletingAllocationId] = useState<string | null>(null);
  const previewStatus = useCollaboratorAllocationPreviewStatus(allocations);

  const isLoadingState = collaboratorsLoading || loadingAllocations;
  const combinedError = collaboratorsError || (allocationsError ? allocationsErrorDetail : null);

  const handleDelete = useCallback(
    async (allocationId: string) => {
      if (!collaboratorId) {
        toastError('Erro ao remover alocação', 'Colaborador não informado.');
        return;
      }

      const confirmRemoval = window.confirm('Deseja realmente remover esta alocação?');
      if (!confirmRemoval) {
        return;
      }

      setDeletingAllocationId(allocationId);
      try {
        await deleteAllocationMutation.mutateAsync({
          collaboratorId,
          allocationId,
        });
        toastSuccess('Alocação removida', 'O registro foi excluído com sucesso.');
      } catch (err) {
        console.error('[allocation-table] delete error', err);
        const message = err instanceof Error ? err.message : 'Tente novamente em alguns instantes.';
        toastError('Erro ao remover alocação', message);
      } finally {
        setDeletingAllocationId(null);
      }
    },
    [collaboratorId, deleteAllocationMutation, toastError, toastSuccess]
  );

  const columnHelper = useMemo(
    () => createColumnHelper<CollaboratorAllocation>(),
    []
  );

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const statusMeta = resolveAllocationStatus(row.original);
          if (!statusMeta) {
            return <span className="text-sm text-gray-500">Sem status</span>;
          }
          return (
            <span
              className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-semibold ${statusMeta.className}`}
            >
              {statusMeta.label}
            </span>
          );
        },
      }),
      columnHelper.accessor('allocationType', {
        id: 'allocationType',
        header: 'Tipo de alocação',
        cell: ({ getValue, row }) => {
          const type = getValue() as AllocationType;
          const meta = ALLOCATION_TYPE_METADATA[type];
          const Icon = ALLOCATION_TYPE_ICON_MAP[type];
          const capacityLabel = formatCapacityLabel(row.original);
          return (
            <div className="flex items-center gap-3">
              {Icon && (
                <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
                  <Icon className="h-5 w-5" aria-hidden="true" />
                </span>
              )}
              <div className="flex flex-col">
                <span className="text-sm font-medium text-gray-900">{meta?.label ?? type}</span>
                {capacityLabel && <span className="text-xs text-gray-500">{capacityLabel}</span>}
                {type === AllocationTypes.Product && row.original.noticeReason && (
                  <span
                    className="line-clamp-2 max-w-xs text-xs text-gray-500"
                    title={row.original.noticeReason}
                  >
                    Motivo: {row.original.noticeReason}
                  </span>
                )}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'period',
        header: 'Período',
        cell: ({ row }) => {
          const startDate = formatTimestampValue(row.original.startDate);
          const hasEndDate = Boolean(row.original.endDate);
          const endDate = hasEndDate
            ? formatTimestampValue(row.original.endDate)
            : 'Sem data de término';

          return (
            <div className="flex flex-col text-sm text-gray-700">
              <span>{startDate}</span>
              <span className="text-xs text-gray-500">
                {hasEndDate ? `até ${endDate}` : endDate}
              </span>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'capacity',
        header: 'Dedicação',
        cell: ({ row }) => (
          <div className="flex flex-col text-sm text-gray-700">
            <span>{formatCapacity(row.original.bucket, row.original.bucketType)}</span>
            {typeof row.original.rate === 'number' && (
              <span className="text-xs text-gray-500">
                {row.original.rate.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
              </span>
            )}
          </div>
        ),
      }),
      columnHelper.accessor('clientName', {
        id: 'clientName',
        header: 'Cliente',
        cell: ({ row }) => {
          const clientName = row.getValue<string>('clientName');
          const clientLogo = row.original.clientLogo;
          const initiative = row.original.projectName;
          if (!clientName) {
            return <span className="text-sm text-gray-500">Sem cliente</span>;
          }
          return (
            <div className="flex items-center gap-3">
              <ClientLogo
                client={{ id: row.original.clientId ?? null, name: clientName, logoUrl: clientLogo ?? null }}
                clientId={row.original.clientId ?? undefined}
                variant={64}
                className="h-10 w-10"
              />
              <div className="flex flex-col">
                <span className="text-sm text-gray-700">{clientName}</span>
                {initiative && (
                  <span className="text-xs text-gray-500">{initiative}</span>
                )}
              </div>
            </div>
          );
        },
      }),
      columnHelper.accessor('responsibleName', {
        id: 'responsibleName',
        header: 'Responsáveis',
        cell: ({ row }) => {
          const responsibleName = row.getValue<string>('responsibleName');
          const responsibleAvatar = row.original.responsibleAvatar;
          const clientResponsible = row.original.clientResponsible;
          if (!responsibleName && !clientResponsible) {
            return <span className="text-sm text-gray-500">Sem responsável</span>;
          }
          return (
            <div className="flex items-center gap-3">
              {responsibleName ? (
                <CollaboratorAvatar
                  photoUrl={responsibleAvatar ?? undefined}
                  name={responsibleName}
                  size="small"
                />
              ) : (
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-500">
                  --
                </span>
              )}
              <div className="flex flex-col">
                {responsibleName && (
                  <span className="text-sm text-gray-700">{responsibleName}</span>
                )}
                {clientResponsible && (
                  <span className="text-xs text-gray-500">Cliente: {clientResponsible}</span>
                )}
              </div>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              className="text-primary-600 hover:text-primary-800"
              onClick={() =>
                collaboratorId &&
                navigate(`/collaborators/${collaboratorId}/allocations/${row.original.id}/edit`)
              }
            >
              <PencilIcon className="h-4 w-4" />
              <span className="sr-only">Editar alocação</span>
            </button>
            <button
              type="button"
              className={`text-red-600 hover:text-red-800 ${deleteAllocationMutation.isLoading && deletingAllocationId === row.original.id
                  ? 'opacity-50 cursor-not-allowed'
                  : ''
                }`}
              onClick={() => handleDelete(row.original.id)}
              disabled={
                deleteAllocationMutation.isLoading && deletingAllocationId === row.original.id
              }
            >
              <TrashIcon className="h-4 w-4" />
              <span className="sr-only">Remover alocação</span>
            </button>
          </div>
        ),
      }),
    ],
    [
      collaboratorId,
      columnHelper,
      navigate,
      handleDelete,
      deleteAllocationMutation.isLoading,
      deletingAllocationId,
    ]
  );

  const table = useReactTable({
    data: allocations,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  // Find the collaborator by ID
  const collaborator = collaborators.find((c) => c.id === collaboratorId);

  const handleBack = () => {
    navigate(-1);
  };

  const renderHeader = () => (
    <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-center gap-3">
        <button
          onClick={handleBack}
          className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-gray-900"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Voltar
        </button>
      </div>
      <button
        type="button"
        onClick={() => collaboratorId && navigate(`/collaborators/${collaboratorId}/allocations/new`)}
        className="btn-primary"
        disabled={!collaboratorId}
      >
        Registrar Alocação
      </button>
    </div>
  );

  // Loading state
  if (isLoadingState) {
    return (
      <>
        {renderHeader()}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
              <span className="ml-2 text-gray-500">Carregando...</span>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Error state
  if (combinedError) {
    return (
      <>
        {renderHeader()}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center py-12">
              <div className="text-red-600 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Erro ao carregar dados</h3>
              <p className="text-gray-500 mb-4">
                {combinedError?.message || 'Ocorreu um erro inesperado. Tente recarregar a página.'}
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Collaborator not found
  if (!collaborator) {
    return (
      <>
        {renderHeader()}
        <div className="bg-white shadow sm:rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <div className="text-center py-12">
              <div className="text-yellow-600 mb-4">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 8.5c-.77.833-.231 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Colaborador não encontrado</h3>
              <p className="text-gray-500">
                O colaborador solicitado não foi encontrado.
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  // Main content
  return (
    <>
      {renderHeader()}

      <div className="bg-white shadow sm:rounded-lg">
        <CollaboratorInfoHeader
          photoUrl={
            collaborator.photoUrl ??
            (collaborator as any).photoURL ??
            null
          }
          name={collaborator.name}
          email={collaborator.email}
        />

        <div className="px-4 py-5 sm:p-6 space-y-6">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <h1 className="text-2xl font-bold text-gray-900">{PAGE_TITLE}</h1>
            <span className="inline-flex items-center rounded-full border border-sky-200 bg-sky-50 px-3 py-1 text-sm font-semibold text-sky-700">
              {previewStatus.label}
            </span>
          </div>

          <TanStackTableView
            table={table}
            isLoading={loadingAllocations}
            loadingMessage="Carregando alocações..."
          />
        </div>
      </div>
    </>
  );
}
